import type { ReactNode } from 'react'
import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { GarmentCard } from '@fitting-room/contracts'
import { firstVisibleIndex } from '../lib/browseView'
import { cardImage } from '../lib/media'
import { uiStrings } from '../lib/strings'
import { text } from '../lib/text'

/**
 * The gowns four to a screen: two across, two down, and the top of the next
 * row showing under them.
 *
 * The owner had asked for one to a screen in September and got it
 * (`docs/20` §4–§6). On 16 October she asked for four, with a way back to one:
 * a feed is how you look at a gown, and a grid is how you look for one. So
 * this is the first thing she sees, and the feed is the switch above away.
 * Two across at every width — "four to a screen" is two side by side, and a
 * third column past a phone's width was nine to a screen, which she did not
 * ask for. A photo opens that gown LARGE over the grid (`GownLightbox`), not
 * in the feed: she is looking for one, and closing it puts her back exactly
 * where she was, because the grid never went anywhere.
 *
 * What is on a card is what she needs to choose from a quarter of a screen:
 * the photo, the gown's name (which for this boutique is its style number,
 * because that is what the import writes there), and the same two marks the
 * feed carries, at the same 44px. The words — silhouette, neckline, sizes —
 * stay behind the feed's own details mark: printed under every card they
 * would be the same gallery-wall price tag the feed took off, four times over.
 *
 * Every frame is an aspect-ratio box, so nothing moves when a photo arrives,
 * and the photo is the narrowest derivative the pipeline made that holds up at
 * this phone's density (`cardImage`).
 */
export function GarmentGrid({
  cards,
  locale,
  index,
  inert = false,
  onScrolled,
  onOpen,
  actions,
}: {
  readonly cards: readonly GarmentCard[]
  readonly locale: string
  /** The gown to open at: the grid scrolls its row to the top when it mounts. */
  readonly index: number
  /** A gown is open large over it: out of reach, for a screen reader as for a thumb. */
  readonly inert?: boolean
  /** Where the grid is, every time it moves: the first gown in view. */
  readonly onScrolled: (first: number) => void
  /** A photo was tapped; `photo` is the button, which focus goes back to. */
  readonly onOpen: (index: number, photo: HTMLElement) => void
  /**
   * The pick marks for one card. The step owns them; see `BrowseStep`.
   * `describedBy` is the id of the card's name, so a mark is announced as
   * "Must try" AND says which gown — a grid has eight of them on a screen.
   *
   * Keep it stable between presses (`useCallback`): every card is memoized on
   * it, and a new function on every scroll is every card drawn again.
   */
  readonly actions: (card: GarmentCard, describedBy: string) => ReactNode
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const frame = useRef<number | null>(null)
  /** See `GarmentDeck`: a photo that did not arrive is a gown with none. */
  const [broken, setBroken] = useState<ReadonlySet<string>>(() => new Set())
  const markBroken = useCallback((src: string) => setBroken((current) => new Set(current).add(src)), [])

  // The parent's callbacks, read through refs so that the cards — memoized on
  // what they are handed — are handed the same function every render.
  const latest = useRef({ onScrolled, onOpen })
  latest.current = { onScrolled, onOpen }

  const report = useCallback(() => {
    frame.current = null
    const scroller = scrollerRef.current
    const list = listRef.current
    if (!scroller || !list) return
    const first = list.children[0]
    if (!(first instanceof HTMLElement)) return
    const columns = getComputedStyle(list).gridTemplateColumns.split(' ').filter(Boolean).length || 1
    const below = list.children[columns]
    const stride = below instanceof HTMLElement ? below.offsetTop - first.offsetTop : first.offsetHeight
    const at = firstVisibleIndex(scroller.scrollTop, {
      stride,
      cardHeight: first.offsetHeight,
      columns,
      count: list.children.length,
    })
    latest.current.onScrolled(at)
  }, [])

  /*
   * Where it opens, set once and before paint, so the grid never shows the top
   * of the catalog for a frame and then jumps to where she was. And reported
   * once, because a grid put at the top fires no scroll event at all.
   */
  const opening = useRef(index)
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    const list = listRef.current
    if (!scroller || !list) return
    const at = opening.current
    const card = list.children[at]
    if (at > 0 && card instanceof HTMLElement) scroller.scrollTop = card.offsetTop
    report()
  }, [report])

  useEffect(
    () => () => {
      if (frame.current !== null) window.cancelAnimationFrame(frame.current)
    },
    [],
  )

  const open = useCallback((at: number, photo: HTMLElement) => latest.current.onOpen(at, photo), [])

  return (
    <div
      ref={scrollerRef}
      className="fr-grid"
      inert={inert ? true : undefined}
      onScroll={() => {
        // Once a frame, however fast the fling: the answer only has to be
        // right by the time she reaches for the switch.
        if (frame.current === null) frame.current = window.requestAnimationFrame(report)
      }}
    >
      <ul ref={listRef} className="fr-grid-list">
        {cards.map((card, cardIndex) => (
          <GridCard
            key={card.garment.id}
            card={card}
            cardIndex={cardIndex}
            locale={locale}
            broken={broken}
            onBroken={markBroken}
            onOpen={open}
            actions={actions}
          />
        ))}
      </ul>
    </div>
  )
}

/**
 * One card. Memoized, because the grid redraws every time the first gown in
 * view changes — which is every row she scrolls past — and none of the cards
 * has changed when it does.
 */
const GridCard = memo(function GridCard({
  card,
  cardIndex,
  locale,
  broken,
  onBroken,
  onOpen,
  actions,
}: {
  readonly card: GarmentCard
  readonly cardIndex: number
  readonly locale: string
  readonly broken: ReadonlySet<string>
  readonly onBroken: (src: string) => void
  readonly onOpen: (index: number, photo: HTMLElement) => void
  readonly actions: (card: GarmentCard, describedBy: string) => ReactNode
}) {
  const t = uiStrings(locale)
  const nameId = useId()
  const name = text(card.garment.name, locale)
  const photo = useMemo(() => cardImage(card.garment.media), [card.garment.media])
  const image = photo && !broken.has(photo.src) ? photo : null
  return (
    <li className="fr-grid-card">
      {/* Labelled by the gown, so a screen reader walking the list hears whose
          photo and marks these are before it hears them. */}
      <article aria-labelledby={nameId} className="fr-grid-article">
        <button
          type="button"
          className="fr-grid-photo"
          aria-label={t.openGown(name)}
          onClick={(event) => onOpen(cardIndex, event.currentTarget)}
        >
          {image ? (
            <img
              src={image.src}
              srcSet={image.srcSet}
              /* Half the column: of the phone, or of 520px past one. */
              sizes="(min-width: 520px) 240px, 50vw"
              alt=""
              /* The first screen is four photos and they are the page;
                 everything under them waits for the scroll. */
              loading={cardIndex < 4 ? 'eager' : 'lazy'}
              decoding="async"
              draggable={false}
              onError={() => onBroken(image.src)}
            />
          ) : (
            <span className="fr-eyebrow">{name}</span>
          )}
        </button>
        <div className="fr-grid-caption">
          <p id={nameId} className="fr-grid-name">
            {name}
          </p>
          <div className="fr-grid-marks">{actions(card, nameId)}</div>
        </div>
      </article>
    </li>
  )
})
